import { TableEntityType } from '@sharefiledev/dc-pilet-sdk';
import { projectsPiletCapabilities } from '@sharefiledev/projects-pilet-sdk';
import {
	Capabilities,
	CapabilityRecord,
	GetNavigationDetailsOptions,
} from '@sharefiledev/sfplugin-capability-contracts';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import { PiletApi } from '@sharefiledev/sharefile-appshell';
import { getTable, getTableRecord } from '../api/dcsApi';

export const TableRecordQueryParamName = 'record';
export const SolutionActiveTabParamName = 'activeTab';

export function getCapabilities(piletApi: PiletApi): CapabilityRecord {
	const getProjectNavigationDetails = async (
		projectRID: string,
		options?: GetNavigationDetailsOptions
	) => {
		const projectsCapabilities = piletApi.sf.getCapability(
			projectsPiletCapabilities.projectsCapabilities
		);
		return await projectsCapabilities.getNavigationDetails(projectRID, options);
	};

	const tableCapabilities: Capabilities = {
		async getNavigationDetails(requestedByResourceId) {
			const tableRID = getResourceRID(requestedByResourceId, TableEntityType.table);

			const [tableDetails, projectsNavigationDetail] = await Promise.all([
				getTable(requestedByResourceId),
				getProjectNavigationDetails(tableRID.toString(), {
					targetExtensionSlot: `${tableRID.entityType}-${tableRID.entityId}`,
				}),
			]);

			return {
				name: tableDetails?.schema?.schemaLabel,
				webAppPath: projectsNavigationDetail?.webAppPath || '',
			};
		},
	};

	const recordCapabilities: Capabilities = {
		async getNavigationDetails(requestedByResourceId, options) {
			const tableRID = getResourceRID(requestedByResourceId, TableEntityType.table);
			const recordRID = getResourceRID(requestedByResourceId, TableEntityType.record);

			const [projectsNavigationDetail, recordDetails] = await Promise.all([
				getProjectNavigationDetails(tableRID.toString(), options),
				getTableRecord(requestedByResourceId),
			]);

			const cleanedPathname = projectsNavigationDetail?.webAppPath
				? cleanupWebAppPath(projectsNavigationDetail.webAppPath, tableRID.entityType)
				: '';

			const searchParams = new URLSearchParams(window.location.search);
			searchParams.set(
				TableRecordQueryParamName,
				`${recordRID.entityType}-${recordRID.entityId}`
			);
			searchParams.set('tab', `${tableRID.entityType}-${tableRID.entityId}`);
			searchParams.set(SolutionActiveTabParamName, tableRID.toString());

			const recordWebPath = `${cleanedPathname}/${tableRID.entityType}-${
				tableRID.entityId
			}?${searchParams.toString()}`;
			return {
				name: recordDetails.title,
				webAppPath: recordWebPath,
			};
		},
	};

	const getResourceRID = (resourceId: string, resourceType: TableEntityType) => {
		const rid = RID.parse(resourceId);
		const tableRID = rid.findEntityType(resourceType);
		if (!tableRID) {
			throw new Error(`No Project resource found for: ${resourceId}`);
		}
		return tableRID;
	};

	const cleanupWebAppPath = (webAppPath: string, tableRIDType: string): string => {
		const url = new URL(webAppPath, window.location.origin);

		if (url.pathname.includes(tableRIDType)) {
			const pathParts = url.pathname.split('/');
			const filteredParts = pathParts.filter(part => !part.includes(tableRIDType));
			const cleanedPath = filteredParts.join('/');
			return cleanedPath;
		}
		return url.pathname;
	};

	return {
		[TableEntityType.table]: tableCapabilities,
		[TableEntityType.record]: recordCapabilities,
	};
}
